/* ============================================================================
 * COHYNSA · Buscador inteligente (predictivo + tolerante a errores)
 * ----------------------------------------------------------------------------
 * Sin dependencias. Reemplaza al predictive-search inerte de Dawn.
 * - Índice cliente de TODOS los productos (/products.json), cacheado en
 *   sessionStorage (TTL 15 min) y precargado al primer foco.
 * - Coincidencia difusa: normaliza (minúsculas + sin acentos), tokens, prefijo,
 *   subcadena, SKU, marca/tipo y DISTANCIA DE EDICIÓN (Levenshtein) para tolerar
 *   errores de dedo ("mangera" → manguera, "conexon" → conexión).
 * - Panel rico: foto + título + marca·tipo + precio; teclado (↑↓ Enter Esc);
 *   fila "Ver todos los resultados" → página de búsqueda nativa.
 * - Funciona en el buscador de escritorio y en el de móvil a la vez.
 * ========================================================================== */
(function () {
  'use strict';
  if (window.__cohynsaSmartSearch) return;
  window.__cohynsaSmartSearch = true;

  var CACHE_KEY = 'cx_search_index_v1';
  var CACHE_TTL = 15 * 60 * 1000;      // 15 min
  var MAX_RESULTS = 7;
  var DEBOUNCE_MS = 110;

  /* ---------- normalización ---------- */
  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')   // quita acentos
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }
  function tokens(s) { return norm(s).split(' ').filter(Boolean); }

  /* ---------- distancia de edición acotada (Levenshtein con corte) ---------- */
  function lev(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    if (la === 0) return lb; if (lb === 0) return la;
    var prev = new Array(lb + 1), cur = new Array(lb + 1), i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur[0] = i; var best = cur[0];
      for (j = 1; j <= lb; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return max + 1;                       // poda: fila mínima ya excede
      var tmp = prev; prev = cur; cur = tmp;
    }
    return prev[lb];
  }

  /* ---------- carga del índice ---------- */
  var INDEX = null, loadingPromise = null;

  function fromCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || (Date.now() - obj.t) > CACHE_TTL || !Array.isArray(obj.d)) return null;
      return obj.d;
    } catch (e) { return null; }
  }
  function toCache(data) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: data })); } catch (e) {}
  }

  function thumb(src) {
    if (!src) return '';
    return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=96';
  }

  function buildRecord(p) {
    var variants = p.variants || [];
    var skus = [], minP = null, anyAvail = false;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v.sku) skus.push(v.sku);
      var pr = parseFloat(v.price);
      if (!isNaN(pr) && (minP === null || pr < minP)) minP = pr;
      if (v.available) anyAvail = true;
    }
    var tagArr = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
    var img = (p.images && p.images[0]) ? p.images[0].src : (p.image ? p.image.src : '');
    var nt = norm(p.title);
    var cot = tagArr.some(function (t) { return /cotiza/i.test(t); });
    return {
      t: p.title, h: p.handle, v: p.vendor || '', ty: p.product_type || '',
      img: thumb(img), p: minP, av: anyAvail, cot: cot, vc: variants.length,
      nt: nt,
      w: nt.split(' ').filter(Boolean),                       // palabras del título
      sk: skus.map(norm),                                      // skus normalizados
      hay: norm([p.vendor, p.product_type, tagArr.join(' '), skus.join(' ')].join(' ')),
    };
  }

  function loadIndex() {
    if (INDEX) return Promise.resolve(INDEX);
    if (loadingPromise) return loadingPromise;
    var cached = fromCache();
    if (cached) { INDEX = cached; return Promise.resolve(INDEX); }

    loadingPromise = (function () {
      var all = [];
      function page(n) {
        return fetch('/products.json?limit=250&page=' + n, { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then(function (j) {
            var prods = (j && j.products) || [];
            for (var i = 0; i < prods.length; i++) all.push(buildRecord(prods[i]));
            if (prods.length === 250 && n < 6) return page(n + 1);   // siguiente página
          });
      }
      return page(1).then(function () {
        INDEX = all; toCache(all); loadingPromise = null;
        return INDEX;
      }).catch(function () { loadingPromise = null; return (INDEX = INDEX || []); });
    })();
    return loadingPromise;
  }

  /* ---------- puntuación difusa ---------- */
  function maxDistFor(len) { return len <= 4 ? 1 : (len <= 7 ? 2 : 3); }

  function tokenScore(tok, rec) {
    var i, w;
    if (rec.nt.indexOf(tok) === 0) return 34;                 // el título empieza con el token
    // palabra del título: exacta o prefijo; la 1ª palabra (sustantivo principal) pesa más
    var bestWord = 0;
    for (i = 0; i < rec.w.length; i++) {
      w = rec.w[i];
      if (w === tok) bestWord = Math.max(bestWord, i === 0 ? 32 : 26);
      else if (w.indexOf(tok) === 0) bestWord = Math.max(bestWord, i === 0 ? 28 : 21);
    }
    if (bestWord) return bestWord;
    for (i = 0; i < rec.sk.length; i++) if (rec.sk[i].indexOf(tok) === 0) return 26; // sku empieza (buscar por clave)
    if (rec.nt.indexOf(tok) >= 0) return 15;                  // subcadena en título
    if (rec.hay.indexOf(tok) >= 0) return 11;                 // subcadena en marca/tipo/tag/sku
    if (tok.length >= 3) {                                    // tolerancia a errores de dedo
      var md = maxDistFor(tok.length), best = md + 1, bi = -1;
      for (i = 0; i < rec.w.length; i++) {                    // typo contra palabras del título
        w = rec.w[i]; if (Math.abs(w.length - tok.length) > md) continue;
        var d = lev(tok, w, md); if (d < best) { best = d; bi = i; }
        if (best === 1) break;
      }
      if (best <= md) { var base = Math.max(4, 13 - best * 3); return bi === 0 ? base + 9 : base; }
      var hw = rec.hay.split(' '), best2 = md + 1;            // typo contra marca/tipo
      for (i = 0; i < hw.length; i++) {
        w = hw[i]; if (!w || Math.abs(w.length - tok.length) > md) continue;
        var d2 = lev(tok, w, md); if (d2 < best2) best2 = d2;
        if (best2 === 1) break;
      }
      if (best2 <= md) return Math.max(3, 10 - best2 * 3);
    }
    return 0;
  }

  function search(q, limit) {
    var qn = norm(q); if (!qn || !INDEX) return [];
    var toks = qn.split(' ').filter(Boolean);
    var res = [];
    for (var i = 0; i < INDEX.length; i++) {
      var rec = INDEX[i], score = 0, all = true;
      for (var k = 0; k < toks.length; k++) {
        var s = tokenScore(toks[k], rec);
        if (s <= 0) { all = false; break; }
        score += s;
      }
      if (!all) continue;
      if (rec.nt.indexOf(qn) === 0) score += 45;              // bonus: título empieza con toda la frase
      else if (rec.nt.indexOf(qn) >= 0) score += 22;
      if (rec.av) score += 3;                                 // leve preferencia a disponibles
      res.push({ r: rec, s: score });
    }
    res.sort(function (a, b) {
      if (b.s !== a.s) return b.s - a.s;
      return a.r.t.length - b.r.t.length;                     // desempate: título más corto
    });
    return res.slice(0, limit || MAX_RESULTS).map(function (x) { return x.r; });
  }

  /* ---------- render ---------- */
  function money(n) {
    if (n == null) return '';
    return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

  function render(container, q, list, loading) {
    if (loading) {
      container.innerHTML = '<div class="cx-ss-state">Buscando…</div>';
      return;
    }
    var html = '';
    if (!list.length) {
      html = '<div class="cx-ss-state">Sin resultados para <b>“' + esc(q) + '”</b>.<br><span>Prueba con la marca, el tipo o revisa la ortografía.</span></div>';
    } else {
      html = '<ul class="cx-ss-list" role="listbox">';
      for (var i = 0; i < list.length; i++) {
        var r = list[i];
        var meta = [r.v, r.ty].filter(Boolean).join(' · ');
        var price = r.cot ? '<span class="cx-ss-cot">Cotizar</span>'
                  : (r.p != null && r.p > 0 ? ((r.vc > 1 ? 'Desde ' : '') + money(r.p)) : '');
        html += '<li class="cx-ss-item" role="option" aria-selected="false">' +
          '<a href="/products/' + esc(r.h) + '">' +
          (r.img ? '<img class="cx-ss-img" src="' + esc(r.img) + '" alt="" loading="lazy">' : '<span class="cx-ss-img cx-ss-noimg"></span>') +
          '<span class="cx-ss-tx"><span class="cx-ss-t">' + esc(r.t) + '</span>' +
          (meta ? '<span class="cx-ss-meta">' + esc(meta) + '</span>' : '') + '</span>' +
          (price ? '<span class="cx-ss-price">' + price + '</span>' : '') +
          '</a></li>';
      }
      html += '</ul>';
    }
    html += '<a class="cx-ss-all" href="/search?q=' + encodeURIComponent(q) + '&type=product&options[prefix]=last">' +
            'Ver todos los resultados de “' + esc(q) + '” <span aria-hidden="true">→</span></a>';
    container.innerHTML = html;
  }

  /* ---------- conexión de cada buscador ---------- */
  function wire(root) {
    var input = root.querySelector('input[type="search"]');
    var panel = root.querySelector('.predictive-search-results-custom');
    if (!input || !panel) return;

    var timer = null, lastQ = '';

    function close() { panel.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); }
    function moveSel(dir) {
      var items = Array.prototype.slice.call(panel.querySelectorAll('.cx-ss-item'));
      if (!items.length) return;
      var cur = panel.querySelector('.cx-ss-item.is-sel');
      var idx = items.indexOf(cur);
      if (cur) cur.classList.remove('is-sel'), cur.setAttribute('aria-selected', 'false');
      idx = dir === 'down' ? (idx + 1) % items.length : (idx <= 0 ? items.length - 1 : idx - 1);
      var next = items[idx];
      next.classList.add('is-sel'); next.setAttribute('aria-selected', 'true');
      next.scrollIntoView({ block: 'nearest' });
    }

    function run() {
      var q = input.value.trim();
      lastQ = q;
      if (!q) { close(); return; }
      input.setAttribute('aria-expanded', 'true');
      if (!INDEX) {
        render(panel, q, [], true);
        loadIndex().then(function () { if (input.value.trim() === lastQ) render(panel, lastQ, search(lastQ), false); });
        return;
      }
      render(panel, q, search(q), false);
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, DEBOUNCE_MS);
    });
    input.addEventListener('focus', function () {
      loadIndex();                          // precarga el índice al primer foco
      if (input.value.trim()) run();
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); moveSel('down'); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); moveSel('up'); }
      else if (e.key === 'Enter') {
        var sel = panel.querySelector('.cx-ss-item.is-sel a');
        if (sel) { e.preventDefault(); window.location.href = sel.getAttribute('href'); }
        // si no hay selección, deja que el form haga la búsqueda nativa (/search?q=)
      } else if (e.key === 'Escape') { close(); input.blur(); }
    });
    // cerrar al hacer clic fuera
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) close();
    });
  }

  /* ---------- PÁGINA /search: reemplaza los resultados nativos por los del motor ----------
     El buscador nativo de Shopify mete ruido (ej. "5 mall" → cualquier cosa con "5":
     adaptadores, conectores…). Aquí pintamos SOLO los productos relevantes, con la
     misma lógica difusa y ordenados por relevancia. */
  function injectPageCSS() {
    if (document.getElementById('cx-sp-css')) return;
    var css = [
      '.template-search.cx-takeover > *:not(.template-search-header):not(.cx-sp){display:none !important;}',
      '.cx-sp{max-width:1280px;margin:0 auto;padding:8px 20px 60px;}',
      '.cx-sp-head{font-family:var(--font-heading-family),sans-serif;font-size:15px;color:#6a7079;margin:0 0 20px;}',
      '.cx-sp-head b{color:var(--accent-color,#CE333C);}',
      '.cx-sp-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:18px;}',
      '.cx-sp-card{display:flex;flex-direction:column;border:1px solid #eef0f2;border-radius:14px;overflow:hidden;background:#fff;text-decoration:none;color:inherit;transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease;}',
      '.cx-sp-card:hover{transform:translateY(-4px);box-shadow:0 14px 30px -18px rgba(0,0,0,.28);border-color:var(--accent-color,#CE333C);}',
      '.cx-sp-img{aspect-ratio:1/1;display:flex;align-items:center;justify-content:center;padding:16px;background:#fff;}',
      '.cx-sp-img img{max-width:100%;max-height:100%;object-fit:contain;mix-blend-mode:multiply;}',
      '.cx-sp-b{border-top:1px solid #f0f2f4;padding:12px 14px 14px;display:flex;flex-direction:column;gap:4px;flex:1;}',
      '.cx-sp-t{font-size:13.5px;font-weight:600;line-height:1.35;color:#16181c;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}',
      '.cx-sp-m{font-size:11.5px;color:#8a9099;}',
      '.cx-sp-p{margin-top:2px;font-size:14px;font-weight:700;color:var(--accent-color,#CE333C);}',
      '.cx-sp-cot{align-self:flex-start;font-size:11px;font-weight:700;color:var(--accent-color,#CE333C);border:1px solid var(--accent-color,#CE333C);border-radius:999px;padding:2px 9px;margin-top:2px;}',
      '.cx-sp-empty{padding:40px 20px;text-align:center;color:#6a7079;font-size:15px;line-height:1.6;}',
      '@media(max-width:600px){.cx-sp-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;}.cx-sp-t{font-size:13px;}}'
    ].join('');
    var st = document.createElement('style'); st.id = 'cx-sp-css'; st.textContent = css;
    document.head.appendChild(st);
  }
  function renderPage(host, q, list) {
    if (!list.length) {
      host.innerHTML = '<div class="cx-sp-empty">Sin resultados para <b>“' + esc(q) + '”</b>.<br>' +
        'Prueba con la marca, el tipo de producto o revisa la ortografía.</div>';
      return;
    }
    var html = '<p class="cx-sp-head"><b>' + list.length + '</b> resultado' + (list.length === 1 ? '' : 's') + ' para “' + esc(q) + '”</p><div class="cx-sp-grid">';
    for (var i = 0; i < list.length; i++) {
      var r = list[i], meta = [r.v, r.ty].filter(Boolean).join(' · ');
      var price = r.cot ? '<span class="cx-sp-cot">Cotizar</span>'
                : (r.p != null && r.p > 0 ? '<span class="cx-sp-p">' + (r.vc > 1 ? 'Desde ' : '') + money(r.p) + '</span>' : '');
      html += '<a class="cx-sp-card" href="/products/' + esc(r.h) + '">' +
        '<span class="cx-sp-img">' + (r.img ? '<img src="' + esc(r.img.replace('width=96', 'width=400')) + '" alt="" loading="lazy">' : '') + '</span>' +
        '<span class="cx-sp-b"><span class="cx-sp-t">' + esc(r.t) + '</span>' +
        (meta ? '<span class="cx-sp-m">' + esc(meta) + '</span>' : '') + price + '</span></a>';
    }
    html += '</div>';
    host.innerHTML = html;
  }
  function takeoverSearchPage() {
    if (!/\/search\/?$/.test(location.pathname)) return;
    var q = '';
    try { q = new URLSearchParams(location.search).get('q') || ''; } catch (e) {}
    if (!q.trim()) return;
    // anclar al .template-search REAL (hay wrappers homónimos): el que contiene el encabezado/facetas
    var header = document.querySelector('.template-search-header');
    var ts = header ? header.closest('.template-search') : null;
    if (!ts) { var ff = document.querySelector('facet-filters-form'); ts = ff ? ff.closest('.template-search') : null; }
    if (!ts) return;
    injectPageCSS();
    ts.classList.add('cx-takeover');                 // oculta lo nativo (facetas + grid + "sin resultados")
    var host = document.createElement('div'); host.className = 'cx-sp';
    host.innerHTML = '<div class="cx-sp-empty">Buscando…</div>';
    if (header && header.parentElement === ts) ts.insertBefore(host, header.nextSibling);
    else ts.appendChild(host);
    loadIndex().then(function () { renderPage(host, q, search(q, 250)); });
  }

  function init() {
    takeoverSearchPage();
    var roots = document.querySelectorAll('predictive-search, .header-search-wrapper, .mobile-search-bar-container');
    // preferir el elemento <predictive-search>; si no, el wrapper
    var seen = [];
    roots.forEach(function (r) {
      // evitar duplicar cuando wrapper contiene predictive-search
      if (seen.some(function (s) { return s.contains(r) || r.contains(s); })) {
        // si ya hay uno anidado, quedarse con el más interno (predictive-search)
      }
    });
    document.querySelectorAll('predictive-search').forEach(function (r) { seen.push(r); wire(r); });
    // por si el markup no usa <predictive-search> en algún lugar
    document.querySelectorAll('.mobile-search-bar-container').forEach(function (r) {
      if (!r.querySelector('predictive-search')) { wire(r); }
    });
    // precarga en reposo (mejora la 1ª búsqueda) sin bloquear la carga
    if ('requestIdleCallback' in window) requestIdleCallback(function () { loadIndex(); }, { timeout: 4000 });
    else setTimeout(loadIndex, 2500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
